import { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFormattingContext } from '../../organisms/FormattingProvider/FormattingContext'
import { useChartContext } from './ChartProvider'

function replaceFormatter (axisLabel, valueFormatter) {
  return {
    ...(axisLabel || {}),
    formatter: valueFormatter
  }
}

function AxisX ({
  axisId,
  show,
  gridIndex,
  alignTicks,
  position,
  offset,
  type,
  name,
  nameLocation,
  nameTextStyle,
  nameGap,
  nameRotate,
  nameTruncate,
  inverse,
  boundaryGap,
  min,
  max,
  scale,
  splitNumber,
  minInterval,
  maxInterval,
  interval,
  logBase,
  startValue,
  silent,
  triggerEvent,
  axisLine,
  axisTick,
  minorTick,
  axisLabel,
  splitLine,
  minorSplitLine,
  splitArea,
  data,
  axisPointer,
  tooltip,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationDelayUpdate,
  animationEasingUpdate,
  zlevel,
  z,
  format
}) {
  const { setKeyConfig } = useChartContext()
  const { formatter } = useFormattingContext()
  const valueFormatter = useCallback((d) => formatter(d, format), [format, formatter])

  useEffect(() => {
    const config = {
      id: axisId,
      show,
      gridIndex,
      alignTicks,
      position,
      offset,
      type,
      name,
      nameLocation,
      nameTextStyle,
      nameGap,
      nameRotate,
      nameTruncate,
      inverse,
      boundaryGap,
      min,
      max,
      scale,
      splitNumber,
      minInterval,
      maxInterval,
      interval,
      logBase,
      startValue,
      silent,
      triggerEvent,
      axisLine,
      axisTick,
      minorTick,
      axisLabel: replaceFormatter(axisLabel, valueFormatter),
      splitLine,
      minorSplitLine,
      splitArea,
      data,
      axisPointer,
      tooltip,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationDelayUpdate,
      animationEasingUpdate,
      zlevel,
      z
    }

    Object.keys(config).forEach((key) => {
      if (typeof config[key] === 'undefined') {
        delete config[key]
      }
    })

    setKeyConfig('xAxis', config)
  }, [
    axisId,
    show,
    gridIndex,
    alignTicks,
    position,
    offset,
    type,
    name,
    nameLocation,
    nameTextStyle,
    nameGap,
    nameRotate,
    nameTruncate,
    inverse,
    boundaryGap,
    min,
    max,
    scale,
    splitNumber,
    minInterval,
    maxInterval,
    interval,
    logBase,
    startValue,
    silent,
    triggerEvent,
    axisLine,
    axisTick,
    minorTick,
    axisLabel,
    splitLine,
    minorSplitLine,
    splitArea,
    data,
    axisPointer,
    tooltip,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationDelayUpdate,
    animationEasingUpdate,
    zlevel,
    z,
    setKeyConfig,
    valueFormatter
  ])

  return null
}

AxisX.propTypes = {
  /** Component ID, not specified by default. If specified, it can be used to refer the component in option or API. */
  axisId: PropTypes.string,

  /** Set this to false to prevent the axis from showing. */
  show: PropTypes.bool,

  /** The index of grid which the x axis belongs to. Defaults to be in the first grid. */
  gridIndex: PropTypes.number,

  /** alignTicks turned on to automatically align ticks when multiple numeric x axes. Only available for axes of type 'value' and 'log'. */
  alignTicks: PropTypes.bool,

  /** The first x axis in grid defaults to be on the bottom of the grid, and the second x axis is on the other side against the first x axis.
   Notice: Set xAxis.axisLine.onZero to false to activate this option. */
  position: PropTypes.oneOf(['top', 'bottom']),

  /** Offset of x axis relative to default position. Useful when multiple x axis has same position value.
   Notice: Set xAxis.axisLine.onZero to false to activate this option. */
  offset: PropTypes.number,

  /** Type of axis.
   Option:
   - 'value' Numerical axis, suitable for continuous data.
   - 'category' Category axis, suitable for discrete category data. Category data can be auto retrieved from series.data or dataset.source, or can be specified via xAxis.data.
   - 'time' Time axis, suitable for continuous time series data. As compared to value axis, it has a better formatting for time and a different tick calculation method. For example, it decides to use month, week, day or hour for tick based on the range of span.
   - 'log' Log axis, suitable for log data. Stacked bar or line series with type: 'log' axes may lead to significant visual errors and may have unintended effects in certain circumstances. Their use should be avoided. */
  type: PropTypes.oneOf(['value', 'time', 'category', 'log']),

  /** Name of axis */
  name: PropTypes.string,

  /** Location of axis name. */
  nameLocation: PropTypes.oneOf(['start', 'middle', 'center', 'end']),

  /** Styling properties */
  nameTextStyle: PropTypes.object,

  /** Gap between axis name and axis line. */
  nameGap: PropTypes.number,

  /** Rotation of axis name. */
  nameRotate: PropTypes.number,

  /** Truncation of the axis name. */
  nameTruncate: PropTypes.shape({
    maxWidth: PropTypes.number,
    ellipsis: PropTypes.bool
  }),

  /** Set this to true to invert the axis. This is a new option available from Echarts 3 and newer. */
  inverse: PropTypes.bool,

  /** The boundary gap on both sides of a coordinate axis. The setting and behavior of category axes and non-category axes are different.
   * The boundaryGap of category axis can be set to either true or false. Default value is set to be true, in which case axisTick is served only as a separation line, and labels and data appear only in the center part of two axis ticks, which is called band.
   * For non-category axis, including time, numerical value, and log axes, boundaryGap is an array of two values, representing the spanning range between minimum and maximum value. The value can be set in numeric value or relative percentage, which becomes invalid after setting min and max.
   * */
  boundaryGap: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),

  /** The minimum value of axis.
   It can be set to a special value 'dataMin' so that the minimum value on this axis is set to be the minimum label.
   It will be automatically computed to make sure axis tick is equally distributed when not set.
   In category axis, it can also be set as the ordinal number. For example, if a catergory axis has data: ['categoryA', 'categoryB', 'categoryC'], and the ordinal 2 represents 'categoryC'. Moreover, it can be set as negative number, like -3. */
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** The maximum value of axis.
   It can be set to a special value 'dataMax' so that the minimum value on this axis is set to be the maximum label.
   It will be automatically computed to make sure axis tick is equally distributed when not set.
   In category axis, it can also be set as the ordinal number. For example, if a catergory axis has data: ['categoryA', 'categoryB', 'categoryC'], and the ordinal 2 represents 'categoryC'. Moreover, it can be set as negative number, like -3. */
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** It is available only in numerical axis, i.e., type: 'value'.
   It specifies whether not to contain zero position of axis compulsively. When it is set to be true, the axis may not contain zero position, which is useful in the scatter chart for both value axes.
   This configuration item is unavailable when the min and max are set. */
  scale: PropTypes.bool,

  /** Number of segments that the axis is split into. Note that this number serves only as a recommendation, and the true segments may be adjusted based on readability.
   This is unavailable for category axis. */
  splitNumber: PropTypes.number,

  /** Minimum gap between split lines.
   For example, it can be set to be 1 to make sure axis label is show as integer. */
  minInterval: PropTypes.number,

  /** Maximum gap between split lines.
   For example, in time axis (type is 'time'), it can be set to be 3600 * 24 * 1000 to make sure that the gap between axis labels is less than or equal to one day. */
  maxInterval: PropTypes.number,

  /** Compulsively set segmentation interval for axis.
   As splitNumber is a recommendation value, the calculated tick may not be the same as expected. In this case, interval should be used along with min and max to compulsively set tickings. But in most cases, we do not suggest using this, our automatic calculation is enough for most situations.
   This is unavailable for 'category' and 'time' axes. Logged value should be passed for type: 'log' axis. */
  interval: PropTypes.number,

  /** Base of logarithm, which is valid only for numeric axes with type: 'log'. */
  logBase: PropTypes.number,

  /** To specify the start value of the axis. */
  startValue: PropTypes.number,

  /** Set this to true, to prevent interaction with the axis. */
  silent: PropTypes.bool,

  /** Set this to true to enable triggering events. */
  triggerEvent: PropTypes.bool,

  /** Settings related to axis line. */
  axisLine: PropTypes.object,

  /** Settings related to axis tick. */
  axisTick: PropTypes.object,

  /** Settings related minor ticks. */
  minorTick: PropTypes.object,

  /** Settings related to axis label. */
  axisLabel: PropTypes.object,

  /** Split line of axis in grid area. */
  splitLine: PropTypes.object,

  /** Minor split lines of axis in the grid area。It will align to the minorTick */
  minorSplitLine: PropTypes.object,

  /** Split area of axis in grid area, not shown by default. */
  splitArea: PropTypes.object,

  /** Axis data (for category) */
  data: PropTypes.array,

  /** axisPointer settings on the axis. */
  axisPointer: PropTypes.object,

  /** The tooltip configuration for xAxis, must set triggerEvent as true and enable global option.tooltip component. */
  tooltip: PropTypes.object,

  /** Whether to enable animation. */
  animation: PropTypes.bool,

  /** Whether to set graphic number threshold to animation. Animation will be disabled when graphic number is larger than threshold. */
  animationThreshold: PropTypes.number,

  /** Duration of the first animation, which supports callback function for different data to have different animation effect: */
  animationDuration: PropTypes.number,

  /** Easing method used for the first animation. Varied easing effects can be found at easing effect example. */
  animationEasing: PropTypes.string,

  /** Delay before updating the first animation, which supports callback function for different data to have different animation effect. */
  animationDelay: PropTypes.number,

  /** Time for animation to complete, which supports callback function for different data to have different animation effect: */
  animationDurationUpdate: PropTypes.number,

  /** Easing method used for animation. */
  animationEasingUpdate: PropTypes.number,

  /** Delay before updating animation, which supports callback function for different data to have different animation effects. */
  animationDelayUpdate: PropTypes.number,

  /** zlevel value of all graphical elements in x axis.
   * zlevel is used to make layers with Canvas. Graphical elements with different zlevel values will be placed in different Canvases,
   * which is a common optimization technique. We can put those frequently changed elements (like those with animations)
   * to a separate zlevel. Notice that too many Canvases will increase memory cost, and should be used carefully on mobile phones to avoid crash.
   * */
  zlevel: PropTypes.number,

  /** z value of all graphical elements in x axis, which controls order of drawing graphical components. Components with smaller z values may be overwritten by those with larger z values.
      z has a lower priority to zlevel, and will not create new Canvas.
   */
  z: PropTypes.number,

  format: PropTypes.string
}

AxisX.defaultProps = {
  show: true,
  position: 'bottom',
  type: 'time',
  format: 'number'
}

export default AxisX
